import React, { useState, useEffect } from 'react';
import AdminLayout from '../../../components/AdminLayout';
import { ConfirmModal, LoadingState, DataTable, FilterBar } from '../../../components/admin/shared';
import { RefreshCw, CheckCircle, XCircle, Clock, AlertCircle, FileText } from 'lucide-react';
import educentialApi from '../../../api/educentialApi';

const BatchUpdates = () => {
  const [operationType, setOperationType] = useState('status'); // 'status' or 'documents'
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // For status updates
  const [statusUpdateConfig, setStatusUpdateConfig] = useState({
    newStatus: '',
    service: 'overseas',
    notifyUsers: true
  });

  // For document updates
  const [documentUpdateConfig, setDocumentUpdateConfig] = useState({
    operation: 'approve', // 'approve' or 'reject'
    note: ''
  });

  const [filters, setFilters] = useState({
    status: '',
    country: ''
  });

  useEffect(() => {
    if (operationType === 'status') {
      loadApplications();
    } else {
      loadDocuments();
    }
  }, [operationType, filters]);

  const loadApplications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.country) params.append('country', filters.country);
      params.append('limit', '100');

      const response = await educentialApi.get(`/api/admin/applications?${params.toString()}`);
      setItems(response.data.data || []);
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    setLoading(true);
    try {
      // Simplified - would need a documents endpoint
      setItems([]);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(item => item.id));
    }
  };

  const handleSelectItem = (itemId) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleBatchUpdate = async () => {
    setShowConfirmModal(false);
    setLoading(true);

    try {
      if (operationType === 'status') {
        await educentialApi.post('/api/admin/operations/batch-status', {
          applicationIds: selectedItems,
          newStatus: statusUpdateConfig.newStatus,
          service: statusUpdateConfig.service,
          notifyUsers: statusUpdateConfig.notifyUsers
        });
      } else {
        await educentialApi.post('/api/admin/operations/batch-documents', {
          documentIds: selectedItems,
          operation: documentUpdateConfig.operation,
          note: documentUpdateConfig.note
        });
      }

      setSuccess(true);
      setSelectedItems([]);

      // Reload items
      if (operationType === 'status') {
        loadApplications();
      } else {
        loadDocuments();
      }

      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error performing batch update:', error);
      alert('Failed to perform batch update. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: 'bg-gray-100 text-gray-800',
      SUBMITTED: 'bg-blue-100 text-blue-800',
      UNDER_REVIEW: 'bg-yellow-100 text-yellow-800',
      OFFER_SECURED: 'bg-green-100 text-green-800',
      VISA_IN_PROGRESS: 'bg-purple-100 text-purple-800',
      COMPLETED: 'bg-teal-100 text-teal-800',
      REJECTED: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badges[status]}`}>
        {status.replace(/_/g, ' ')}
      </span>
    );
  };

  const applicationColumns = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          checked={selectedItems.length === items.length && items.length > 0}
          onChange={handleSelectAll}
          className="rounded border-gray-300"
        />
      ),
      render: (app) => (
        <input
          type="checkbox"
          checked={selectedItems.includes(app.id)}
          onChange={() => handleSelectItem(app.id)}
          onClick={(e) => e.stopPropagation()}
          className="rounded border-gray-300"
        />
      )
    },
    {
      key: 'id',
      label: 'ID',
      render: (app) => <span className="font-mono text-xs">#{app.id}</span>
    },
    {
      key: 'studentName',
      label: 'Student',
      render: (app) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{app.studentName || 'N/A'}</div>
          <div className="text-xs text-gray-500">{app.userEmail}</div>
        </div>
      )
    },
    {
      key: 'country',
      label: 'Country',
      render: (app) => <span className="text-sm">{app.country}</span>
    },
    {
      key: 'status',
      label: 'Status',
      render: (app) => getStatusBadge(app.status)
    },
    {
      key: 'createdAt',
      label: 'Submitted',
      render: (app) => (
        <span className="text-sm text-gray-600">
          {new Date(app.createdAt).toLocaleDateString()}
        </span>
      )
    }
  ];

  const filterConfig = [
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      value: filters.status,
      options: [
        { value: 'SUBMITTED', label: 'Submitted' },
        { value: 'UNDER_REVIEW', label: 'Under Review' },
        { value: 'OFFER_SECURED', label: 'Offer Secured' },
        { value: 'VISA_IN_PROGRESS', label: 'Visa in Progress' },
        { value: 'COMPLETED', label: 'Completed' },
        { value: 'REJECTED', label: 'Rejected' }
      ]
    },
    {
      name: 'country',
      label: 'Country',
      type: 'select',
      value: filters.country,
      options: [
        { value: 'UK', label: 'United Kingdom' },
        { value: 'US', label: 'United States' },
        { value: 'Canada', label: 'Canada' },
        { value: 'Australia', label: 'Australia' }
      ]
    }
  ];

  if (loading && items.length === 0) {
    return (
      <AdminLayout title="Batch Updates">
        <LoadingState message="Loading items..." />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Batch Updates">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Batch Updates</h2>
          <p className="text-gray-600 mt-1">Perform bulk operations on applications and documents</p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <div>
              <p className="text-green-900 font-medium">Batch update completed successfully!</p>
              <p className="text-green-700 text-sm">
                {selectedItems.length} items were updated.
              </p>
            </div>
          </div>
        )}

        {/* Operation Type Selector */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Operation Type</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={() => {
                setOperationType('status');
                setSelectedItems([]);
              }}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                operationType === 'status'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${operationType === 'status' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                  <RefreshCw className={`w-5 h-5 ${operationType === 'status' ? 'text-blue-600' : 'text-gray-600'}`} />
                </div>
                <div>
                  <p className={`font-medium ${operationType === 'status' ? 'text-blue-900' : 'text-gray-900'}`}>
                    Batch Status Update
                  </p>
                  <p className={`text-sm mt-1 ${operationType === 'status' ? 'text-blue-700' : 'text-gray-600'}`}>
                    Update status for multiple applications
                  </p>
                </div>
              </div>
            </button>

            <button
              onClick={() => {
                setOperationType('documents');
                setSelectedItems([]);
              }}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                operationType === 'documents'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${operationType === 'documents' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                  <FileText className={`w-5 h-5 ${operationType === 'documents' ? 'text-blue-600' : 'text-gray-600'}`} />
                </div>
                <div>
                  <p className={`font-medium ${operationType === 'documents' ? 'text-blue-900' : 'text-gray-900'}`}>
                    Batch Document Review
                  </p>
                  <p className={`text-sm mt-1 ${operationType === 'documents' ? 'text-blue-700' : 'text-gray-600'}`}>
                    Approve or reject multiple documents
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Filters */}
        {operationType === 'status' && (
          <FilterBar
            filters={filterConfig}
            onFilterChange={(name, value) => setFilters(prev => ({ ...prev, [name]: value }))}
            onClear={() => setFilters({ status: '', country: '' })}
          />
        )}

        {/* Selection Actions */}
        {selectedItems.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900">
                {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} selected
              </span>

              {operationType === 'status' ? (
                <div className="flex items-center space-x-3">
                  <select
                    value={statusUpdateConfig.newStatus}
                    onChange={(e) => setStatusUpdateConfig({ ...statusUpdateConfig, newStatus: e.target.value })}
                    className="border-blue-300 rounded-md text-sm"
                  >
                    <option value="">Select new status</option>
                    <option value="UNDER_REVIEW">Under Review</option>
                    <option value="OFFER_SECURED">Offer Secured</option>
                    <option value="VISA_IN_PROGRESS">Visa in Progress</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="REJECTED">Rejected</option>
                  </select>

                  <label className="flex items-center space-x-2 text-sm text-blue-900">
                    <input
                      type="checkbox"
                      checked={statusUpdateConfig.notifyUsers}
                      onChange={(e) => setStatusUpdateConfig({ ...statusUpdateConfig, notifyUsers: e.target.checked })}
                      className="rounded border-blue-300"
                    />
                    <span>Notify users</span>
                  </label>

                  <button
                    onClick={() => setShowConfirmModal(true)}
                    disabled={!statusUpdateConfig.newStatus}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Update Status
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <select
                    value={documentUpdateConfig.operation}
                    onChange={(e) => setDocumentUpdateConfig({ ...documentUpdateConfig, operation: e.target.value })}
                    className="border-blue-300 rounded-md text-sm"
                  >
                    <option value="approve">Approve</option>
                    <option value="reject">Reject</option>
                  </select>

                  {documentUpdateConfig.operation === 'reject' && (
                    <input
                      type="text"
                      placeholder="Rejection reason..."
                      value={documentUpdateConfig.note}
                      onChange={(e) => setDocumentUpdateConfig({ ...documentUpdateConfig, note: e.target.value })}
                      className="border-blue-300 rounded-md text-sm"
                    />
                  )}

                  <button
                    onClick={() => setShowConfirmModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    {documentUpdateConfig.operation === 'approve' ? 'Approve' : 'Reject'} Documents
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Items Table */}
        {operationType === 'status' ? (
          <DataTable
            columns={applicationColumns}
            data={items}
            loading={loading}
            emptyMessage="No applications found. Try adjusting your filters."
          />
        ) : (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Document batch operations coming soon</p>
            <p className="text-sm text-gray-500 mt-2">
              This feature will allow you to approve or reject multiple documents at once.
            </p>
          </div>
        )}

        {/* Info Box */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-yellow-900">Batch Update Guidelines</p>
              <ul className="text-sm text-yellow-800 mt-2 space-y-1">
                <li>• Select multiple items using checkboxes</li>
                <li>• Choose the operation you want to perform</li>
                <li>• Review your selection carefully before confirming</li>
                <li>• Users will be notified if the "Notify users" option is enabled</li>
                <li>• All actions are logged for audit purposes</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        <ConfirmModal
          isOpen={showConfirmModal}
          title={`Confirm Batch ${operationType === 'status' ? 'Status Update' : 'Document Review'}`}
          message={
            operationType === 'status'
              ? `Are you sure you want to update ${selectedItems.length} application(s) to "${statusUpdateConfig.newStatus}"?`
              : `Are you sure you want to ${documentUpdateConfig.operation} ${selectedItems.length} document(s)?`
          }
          confirmText="Confirm"
          cancelText="Cancel"
          onConfirm={handleBatchUpdate}
          onCancel={() => setShowConfirmModal(false)}
        />
      </div>
    </AdminLayout>
  );
};

export default BatchUpdates;
